import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  Upload
} from 'antd'
import {
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
  UpOutlined,
  UploadOutlined
} from '@ant-design/icons'
import {
  IDENTIFIER_LABELS,
  MARKET_LABELS,
  MARKET_ORDER,
  activationSentences,
  componentsCostUsd,
  estimateCostIn,
  estimateMargin,
  marginColor,
  money,
  percent,
  usd
} from './utils'

const { TextArea } = Input

// Everything about one package, in one drawer. The owner's screen: this is where he decides what a
// report costs a customer, which is the one decision the whole service is built around.
//
// **The live estimate (Req 2.3).** As he types a price or changes the components, the margin
// updates — computed from the API's own `costUsd` and the rate the API returned, never from a rate
// this browser fetched. Two sources for one exchange rate is two different margins on one screen.
// The estimate is labelled as an estimate until he saves, because the saved figure is the server's.
const ProductFormDrawer = ({
  open,
  product,
  services,
  currencies,
  exchangeRate,
  saving,
  onClose,
  onSave,
  onUploadPreview,
  onRemovePreview
}) => {
  const [form] = Form.useForm()
  const [features, setFeatures] = useState([])
  const [components, setComponents] = useState([])
  const [priceAmount, setPriceAmount] = useState(null)
  const [priceCurrency, setPriceCurrency] = useState('GHS')
  const isNew = !product || !product.id

  useEffect(() => {
    if (!open) return
    const row = product || {}
    setFeatures(row.features || [])
    setComponents(
      (row.components || []).map(component => ({
        serviceId: component.serviceId,
        label: component.label
      }))
    )
    setPriceAmount(row.priceAmount === null || row.priceAmount === undefined ? null : Number(row.priceAmount))
    setPriceCurrency(row.priceCurrency || (currencies || ['GHS'])[0] || 'GHS')
    form.setFieldsValue({
      key: row.key || '',
      name: row.name || '',
      tagline: row.tagline || '',
      description: row.description || '',
      market: row.market || 'north_america',
      identifierKind: row.identifierKind || 'vin',
      deliveryEstimate: row.deliveryEstimate || '',
      position: row.position === undefined ? 0 : row.position,
      recommended: !!row.recommended,
      minMarginPercent: row.minMarginPercent
    })
  }, [open, product, currencies, form])

  // The cost of whatever is picked RIGHT NOW, which is what makes the estimate live.
  const draftCostUsd = componentsCostUsd(
    components.map(component => component.serviceId),
    services
  )
  const estimate = estimateMargin({
    priceAmount,
    priceCurrency,
    costUsd: draftCostUsd,
    ghsToUsd: (exchangeRate || {}).ghsToUsd
  })
  const draftCostInCurrency = estimateCostIn(
    draftCostUsd,
    priceCurrency,
    (exchangeRate || {}).ghsToUsd
  )

  const savedErrors = isNew ? [] : activationSentences(product.activationErrors)

  const moveComponent = (index, delta) => {
    const next = [...components]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    const [row] = next.splice(index, 1)
    next.splice(target, 0, row)
    setComponents(next)
  }

  const moveFeature = (index, delta) => {
    const next = [...features]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    const [row] = next.splice(index, 1)
    next.splice(target, 0, row)
    setFeatures(next)
  }

  const submit = async () => {
    const values = await form.validateFields()
    onSave({
      ...values,
      features: features.filter(feature => feature && feature.trim()),
      // Sent as a two-decimal string: money never crosses the wire as a float.
      priceAmount: priceAmount === null || priceAmount === undefined ? null : Number(priceAmount).toFixed(2),
      priceCurrency,
      components: components
        .filter(component => component.serviceId)
        .map(component => ({
          serviceId: component.serviceId,
          label: component.label || ''
        }))
    })
  }

  return (
    <Drawer
      title={isNew ? 'New report package' : product.name}
      width={720}
      visible={open}
      onClose={onClose}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={submit}>
            Save
          </Button>
        </Space>
      }
    >
      {savedErrors.length ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This package cannot go on sale yet"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
              {savedErrors.map(sentence => (
                <li key={sentence}>{sentence}</li>
              ))}
            </ul>
          }
        />
      ) : null}

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'What is this package called?' }]}
            >
              <Input placeholder="USA & Canada History Check" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Key"
              name="key"
              extra="The app asks for the package by this. Changing it on a live package breaks nothing that has already been paid for, but the app's links will miss."
              rules={[{ required: true, message: 'A key is required.' }]}
            >
              <Input placeholder="usa-history" disabled={!isNew} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Tagline" name="tagline">
          <Input placeholder="Carfax and AutoCheck, together" maxLength={120} />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea rows={4} placeholder="What the customer gets, in their words." />
        </Form.Item>

        <Form.Item label="What it includes" extra="Shown as a tick list. Drag order matters.">
          {features.map((feature, index) => (
            <Space key={index} style={{ display: 'flex', marginBottom: 8 }} align="start">
              <Input
                value={feature}
                style={{ width: 430 }}
                onChange={event => {
                  const next = [...features]
                  next[index] = event.target.value
                  setFeatures(next)
                }}
              />
              <Button
                icon={<UpOutlined />}
                disabled={index === 0}
                onClick={() => moveFeature(index, -1)}
              />
              <Button
                icon={<DownOutlined />}
                disabled={index === features.length - 1}
                onClick={() => moveFeature(index, 1)}
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => setFeatures(features.filter((_, at) => at !== index))}
              />
            </Space>
          ))}
          <Button icon={<PlusOutlined />} onClick={() => setFeatures([...features, ''])}>
            Add a line
          </Button>
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Market" name="market">
              <Select>
                {MARKET_ORDER.map(market => (
                  <Select.Option key={market} value={market}>
                    {MARKET_LABELS[market]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Number it needs"
              name="identifierKind"
              extra="Every report in the package must take this kind of number."
            >
              <Select disabled={!isNew}>
                {Object.keys(IDENTIFIER_LABELS).map(kind => (
                  <Select.Option key={kind} value={kind}>
                    {IDENTIFIER_LABELS[kind]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Position" name="position">
              <InputNumber min={0} max={32767} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="How long it takes" name="deliveryEstimate">
          <Input placeholder="Usually ready in 5–15 minutes" maxLength={120} />
        </Form.Item>

        {/* --- the money ------------------------------------------------------------------ */}
        <Card
          size="small"
          title="What the customer pays"
          style={{ marginBottom: 16 }}
          bordered
        >
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item label="Price" style={{ marginBottom: 8 }}>
                <InputNumber
                  value={priceAmount}
                  onChange={setPriceAmount}
                  min={0}
                  step={10}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="200.00"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Currency" style={{ marginBottom: 8 }}>
                <Select value={priceCurrency} onChange={setPriceCurrency}>
                  {(currencies || ['GHS', 'USD']).map(currency => (
                    <Select.Option key={currency} value={currency}>
                      {currency}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Margin floor"
                name="minMarginPercent"
                style={{ marginBottom: 8 }}
                extra="Blank uses the service-wide floor."
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="30" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 8 }}>
            <Space size="large" wrap>
              <span>
                <Typography.Text type="secondary">Costs us </Typography.Text>
                <Typography.Text strong>
                  {draftCostUsd === null ? '—' : usd(draftCostUsd)}
                </Typography.Text>
                {draftCostInCurrency === null ? null : (
                  <Typography.Text type="secondary">
                    {' '}
                    ({money(draftCostInCurrency, priceCurrency)})
                  </Typography.Text>
                )}
              </span>
              <span>
                <Typography.Text type="secondary">Margin </Typography.Text>
                {estimate ? (
                  <Tag color={marginColor(estimate.percent, form.getFieldValue('minMarginPercent'))}>
                    {money(estimate.amount, priceCurrency)} · {percent(estimate.percent)}
                  </Tag>
                ) : (
                  <Tag>—</Tag>
                )}
              </span>
              <Typography.Text type="secondary" italic>
                Estimate until you save
              </Typography.Text>
            </Space>
          </div>
          {(exchangeRate || {}).ghsToUsd ? (
            <div style={{ marginTop: 4 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Worked out at {Number(exchangeRate.ghsToUsd).toFixed(2)} cedis to the dollar, the
                rate the server last stored.
              </Typography.Text>
            </div>
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              message="No exchange rate stored yet, so a cedi margin cannot be worked out. The package can still be priced and sold."
            />
          )}
        </Card>

        {/* --- the components --------------------------------------------------------------- */}
        <Card size="small" title="Reports in this package" style={{ marginBottom: 16 }} bordered>
          {components.map((component, index) => (
            <Space key={index} style={{ display: 'flex', marginBottom: 8 }} align="start" wrap>
              <Select
                showSearch
                optionFilterProp="children"
                value={component.serviceId}
                style={{ width: 250 }}
                placeholder="Pick a supplier report"
                onChange={value => {
                  const next = [...components]
                  const service = (services || []).find(row => row.id === value)
                  next[index] = {
                    serviceId: value,
                    label: next[index].label || (service ? service.name : '')
                  }
                  setComponents(next)
                }}
              >
                {(services || []).map(service => (
                  <Select.Option key={service.id} value={service.id}>
                    {service.family}/{service.key} ·{' '}
                    {service.costUsd === null ? 'no cost yet' : usd(service.costUsd)}
                  </Select.Option>
                ))}
              </Select>
              <Input
                value={component.label}
                style={{ width: 220 }}
                placeholder="What the customer sees"
                onChange={event => {
                  const next = [...components]
                  next[index] = { ...next[index], label: event.target.value }
                  setComponents(next)
                }}
              />
              <Button
                icon={<UpOutlined />}
                disabled={index === 0}
                onClick={() => moveComponent(index, -1)}
              />
              <Button
                icon={<DownOutlined />}
                disabled={index === components.length - 1}
                onClick={() => moveComponent(index, 1)}
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => setComponents(components.filter((_, at) => at !== index))}
              />
            </Space>
          ))}
          <Button
            icon={<PlusOutlined />}
            onClick={() => setComponents([...components, { serviceId: null, label: '' }])}
          >
            Add a report
          </Button>
        </Card>

        <Form.Item label="Recommended" name="recommended" valuePropName="checked">
          <Switch />
        </Form.Item>

        {isNew ? null : (
          <Card size="small" title="Preview image" bordered>
            {product.previewImageUrl ? (
              <div style={{ marginBottom: 8 }}>
                <img
                  src={product.previewImageUrl}
                  alt="Sample report"
                  style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 4 }}
                />
              </div>
            ) : null}
            <Space>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={file => {
                  onUploadPreview(file)
                  return false
                }}
              >
                <Button icon={<UploadOutlined />}>
                  {product.previewImageUrl ? 'Replace' : 'Upload'}
                </Button>
              </Upload>
              {product.previewImageUrl ? (
                <Button danger onClick={onRemovePreview}>
                  Remove
                </Button>
              ) : null}
            </Space>
            <div style={{ marginTop: 8 }}>
              <Typography.Text type="secondary">
                A sample car&apos;s report, blurred — never a customer&apos;s.
              </Typography.Text>
            </div>
          </Card>
        )}
      </Form>
    </Drawer>
  )
}

ProductFormDrawer.propTypes = {
  open: PropTypes.bool,
  product: PropTypes.object,
  services: PropTypes.array,
  currencies: PropTypes.array,
  exchangeRate: PropTypes.object,
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onUploadPreview: PropTypes.func,
  onRemovePreview: PropTypes.func
}

export default ProductFormDrawer
